import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';

import { ReportQueueItem } from '../../../interfaces/agricola.interface';
import { AgricolaService } from '../../../services/agricola.service';

const REFRESH_INTERVAL_MS = 10000;
const PENDING_STATUSES = [1, 2, 3];

const STATUS_LABELS: Record<number, string> = {
  1: 'Pendiente',
  2: 'En proceso',
  3: 'Subiendo',
  4: 'Finalizado',
  5: 'Fallido'
};

const STATUS_DESCRIPTIONS: Record<number, string> = {
  1: 'La solicitud está en cola.',
  2: 'Generando el reporte.',
  3: 'Subiendo el archivo.',
  4: 'El reporte está listo para descargar.',
  5: 'La generación del reporte falló.'
};

@Component({
  selector: 'app-agricola-report-queue',
  templateUrl: './agricola-report-queue.component.html',
  styleUrls: ['./agricola-report-queue.component.css'],
  standalone: false
})
export class AgricolaReportQueueComponent implements OnInit, OnDestroy {
  pageSize = 10;
  readonly pageSizeOptions = [10, 25, 50];

  searchId = '';
  items: ReportQueueItem[] = [];
  total = 0;
  page = 1;
  loading = false;
  errorMessage = '';

  private refreshTimer: ReturnType<typeof setInterval> | null = null;

  constructor(
    private readonly agricola: AgricolaService,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    this.stopAutoRefresh();
  }

  get filteredItems(): ReportQueueItem[] {
    const term = this.searchId.trim();
    if (!term) {
      return this.items;
    }
    return this.items.filter(item => String(item.id).includes(term));
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.total / this.pageSize));
  }

  get pageNumbers(): number[] {
    return Array.from({ length: this.totalPages }, (_, i) => i + 1);
  }

  load(page = this.page): void {
    this.page = page;
    this.loading = true;
    this.errorMessage = '';

    this.agricola.getReportQueue({}, page, this.pageSize).subscribe({
      next: response => {
        this.items = response.data || [];
        this.total = response.total || 0;
        this.loading = false;
        this.syncAutoRefresh();
      },
      error: () => {
        this.items = [];
        this.total = 0;
        this.loading = false;
        this.errorMessage = 'No se pudieron cargar las solicitudes. Intenta nuevamente.';
        this.stopAutoRefresh();
      }
    });
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.page) {
      return;
    }
    this.load(page);
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.load(1);
  }

  search(): void {
    // Server returns the queue; the request-number field filters it client-side.
    this.page = 1;
  }

  statusLabel(status: number): string {
    return STATUS_LABELS[status] ?? 'Desconocido';
  }

  statusDescription(status: number): string {
    return STATUS_DESCRIPTIONS[status] ?? '';
  }

  reportName(item: ReportQueueItem): string {
    return item.type === 21 ? 'Reporte de ventas' : 'Reporte';
  }

  dateRange(item: ReportQueueItem): string {
    const filter = item.filter as { from_date?: string; to_date?: string } | null | undefined;
    if (filter?.from_date && filter?.to_date) {
      return `${filter.from_date} — ${filter.to_date}`;
    }
    return '—';
  }

  canDownload(item: ReportQueueItem): boolean {
    return item.status === 4 && !!item.attachment;
  }

  download(item: ReportQueueItem): void {
    if (!item.attachment) {
      return;
    }
    this.agricola.downloadAttachment(item.attachment.id, 1)
      .catch(() => {
        this.errorMessage = 'No se pudo descargar el reporte. Intenta nuevamente.';
      });
  }

  goBack(): void {
    this.router.navigate(['/agricola']);
  }

  private syncAutoRefresh(): void {
    const hasPending = this.items.some(item => PENDING_STATUSES.includes(item.status));
    if (hasPending) {
      this.startAutoRefresh();
    } else {
      this.stopAutoRefresh();
    }
  }

  private startAutoRefresh(): void {
    if (this.refreshTimer) {
      return;
    }
    this.refreshTimer = setInterval(() => this.load(this.page), REFRESH_INTERVAL_MS);
  }

  private stopAutoRefresh(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }
}
